import { useMemo } from 'react';
import { AssortmentPrices, AssortmentView } from '@retail/my-assortment/types';
import { StackedTextCell } from '@retail/components';
import { dataGridBoldClass } from '@shared/styles';
import { Stack, Tooltip } from '@mui/material';
import { DecreaseIcon, IncreaseIcon } from '@shared/custom-icons';
import { formatPrice, getDiscontinuationStatus } from '@retail/products/utils';
import { useMyAssortmentTranslation } from '@retail/my-assortment/i18n';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid-premium';
import { calculatePercentChange, getLLDate } from '@retail/utils';
import { ExternalItemSource, PricePerUnit } from '@retail/products/components';
import { StarBorder } from '@mui/icons-material';
import { OrgUnitType } from '@retail/mgpris/config-types';
import { useItemInfoColumns, usePriceListColumns } from './AssortmentTable';
import { useConditionColumns, useItemPriceColumns } from './AssortmentTable/AssortmentColumns';
import { AssortmentTableActions } from './AssortmentTable/AssortmentTableActions';
import { useFilters } from '@retail/products/hooks';
import { baseAssortmentFilters, BaseAssortmentFilters } from '@retail/my-assortment/utils';
import { SubAssortmentChip } from './SubAssortmentChip';

type GridColAssortmentItem = GridColDef<AssortmentPrices>;

export interface RowPriceProps {
  includeVat: boolean;
  enableSortable?: boolean;
}

interface ShowColumnProps {
  selectedView: AssortmentView;
  showBracketPriceColumns: boolean;
  orgUnitType: OrgUnitType;
  isPriceSuggestionEnabled: boolean;
  showMainSupplierName: boolean;
}

interface Props {
  disableItemRemoval?: boolean;
  isPriceCalculationEnabled: boolean;
  externalItemSource: ExternalItemSource;
  selectPurchaseAgreementItemToEdit?: (agreement?: AssortmentPrices) => void;
  rowIdForCalculusCard?: (rowId: number) => void;
  rowIdForProductCard: (rowId: number) => void;
  editItem?: (item: AssortmentPrices) => void;
  editPrices?: (item: AssortmentPrices) => void;
  deleteItem?: (mgSupplierItemId: number) => void;
}

type ColumnConfig = {
  show: (props: ShowColumnProps) => boolean;
  getColumn: () => GridColAssortmentItem;
};

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};

export const useAssortmentPricesColumns = ({
  includeVat,
  isPriceCalculationEnabled,
  externalItemSource,
  rowIdForCalculusCard,
  rowIdForProductCard,
  editItem,
  editPrices,
  deleteItem,
}: Props & RowPriceProps): ColumnConfig[] => {
  const { t } = useMyAssortmentTranslation();
  const { i18n } = useTranslation();
  const { setFilters, updateFilter } = useFilters<BaseAssortmentFilters>(baseAssortmentFilters);

  const {
    itemStatusColumn,
    itemIdsColumn,
    itemNameColumn,
    supplierColumn,
    productGroupColumn,
    isStockedColumn,
    discountGroupColumn,
  } = useItemInfoColumns<AssortmentPrices>({
    externalItemSource,
    selectSupplier: (supplierNumber: number) => setFilters({ supplierNumber: supplierNumber + '' }),
    selectProductGroup: (productGroupNumber: string) =>
      updateFilter('productGroupNumbers', productGroupNumber),
    selectDiscountGroup: (discountGroupCode: string, supplierNumber: number) =>
      setFilters({ discountGroupCodes: [discountGroupCode], supplierNumber: supplierNumber + '' }),
  });

  const {
    purchasePriceColumn,
    costPriceInSalesUnitColumn,
    costPriceColumn,
    salesPriceColumn,
    salesPriceCoverageColumn,
    futurePurchasePriceColumn,
    futureSalesPriceColumn,
    priceSourceColumn,
  } = useItemPriceColumns({
    includeVat,
  });

  const { purchaseConditionColumn, freightConditionColumn } = useConditionColumns();

  const {
    MGLSalesCoverageColumn,
    MGLSalesPriceColumn,
    customerMarkupFactorColumn,
    customerSalesPriceColumn,
    customerSalesCoverageColumn,
  } = usePriceListColumns({
    includeVat,
  });

  return useMemo(
    () => [
      {
        show: ({ selectedView }) => selectedView === 'ASSORTMENT_FULL',
        getColumn: () => itemStatusColumn,
      },
      {
        show: () => true,
        getColumn: () => itemIdsColumn,
      },
      {
        show: () => true,
        getColumn: () => itemNameColumn,
      },
      {
        show: ({ selectedView }) => selectedView === 'ASSORTMENT_FULL',
        getColumn: () => ({
          ...baseColumnProps,
          sortable: false,
          field: 'supplierItemPriority',
          headerAlign: 'center',
          minWidth: 30,
          width: 30,
          headerName: '',
          renderCell: ({ row }) =>
            row.priority.prioritised && (
              <Tooltip title={t('myAssortment.priority.mainPrioSupplier')} placement="top" arrow>
                <StarBorder color="success" fontSize="small" />
              </Tooltip>
            ),
        }),
      },
      {
        show: ({ selectedView }) => selectedView !== 'ASSORTMENT_SIMPLE',
        getColumn: () => supplierColumn,
      },
      {
        show: ({ selectedView }) => selectedView === 'ASSORTMENT_FULL',
        getColumn: () => productGroupColumn,
      },
      {
        show: ({ selectedView }) => selectedView === 'ASSORTMENT_FULL',
        getColumn: () => discountGroupColumn,
      },
      {
        show: ({ selectedView, orgUnitType }) =>
          selectedView === 'ASSORTMENT_FULL' && orgUnitType !== OrgUnitType.RETAIL_BRAND,
        getColumn: () => purchaseConditionColumn,
      },
      {
        show: ({ selectedView, orgUnitType }) =>
          selectedView !== 'COMPETITOR_PRICES' && orgUnitType !== OrgUnitType.RETAIL_BRAND,
        getColumn: () => purchasePriceColumn,
      },
      {
        show: ({ selectedView, orgUnitType }) =>
          selectedView !== 'COMPETITOR_PRICES' && orgUnitType !== OrgUnitType.RETAIL_BRAND,
        getColumn: () => futurePurchasePriceColumn,
      },
      {
        show: ({ selectedView, orgUnitType }) =>
          selectedView === 'PRICE_CHANGES' && orgUnitType !== OrgUnitType.RETAIL_BRAND,
        getColumn: () => ({
          ...baseColumnProps,
          field: 'purchasePriceChange',
          headerName: t('myAssortment.columns.change'),
          renderCell: ({ row }) => {
            const purchasePrice = row.pricesExVat.purchasePrice;
            const futurePurchasePrice = row.pricesExVat.futurePurchasePrice;

            if (!purchasePrice || !futurePurchasePrice) return '-';
            else if (!!purchasePrice && !!futurePurchasePrice) {
              const changeInValue = futurePurchasePrice - purchasePrice;
              const percentChangeInValue =
                formatPrice(
                  calculatePercentChange(purchasePrice, futurePurchasePrice),
                  i18n.language
                ) + '%';
              return (
                <Stack flexDirection="row" alignItems="center" gap={1}>
                  {changeInValue > 0 ? (
                    <IncreaseIcon color="warning" fontSize="small" />
                  ) : changeInValue < 0 ? (
                    <DecreaseIcon color="success" fontSize="small" />
                  ) : null}
                  <StackedTextCell
                    topLine={t('myAssortment.recommendedPrices.value', {
                      value: formatPrice(changeInValue, i18n.language),
                    })}
                    bottomLine={percentChangeInValue}
                  />
                </Stack>
              );
            }

            return t('myAssortment.recommendedPrices.value', { value: 0 });
          },
          valueGetter: (_, row) => {
            const purchasePrice = row.pricesExVat.purchasePrice;
            const futurePurchasePrice = row.pricesExVat.futurePurchasePrice;

            return !!futurePurchasePrice && !!purchasePrice
              ? futurePurchasePrice - purchasePrice
              : undefined;
          },
        }),
      },
      {
        show: ({ selectedView, orgUnitType }) =>
          selectedView === 'ASSORTMENT_FULL' && orgUnitType !== OrgUnitType.RETAIL_BRAND,
        getColumn: () => freightConditionColumn,
      },
      {
        show: ({ selectedView, orgUnitType }) =>
          selectedView === 'ASSORTMENT_FULL' && orgUnitType !== OrgUnitType.RETAIL_BRAND,
        getColumn: () => costPriceInSalesUnitColumn,
      },
      {
        show: ({ selectedView, orgUnitType }) =>
          selectedView === 'ASSORTMENT_FULL' && orgUnitType !== OrgUnitType.RETAIL_BRAND,
        getColumn: () => costPriceColumn,
      },
      {
        show: ({ orgUnitType }) =>
          orgUnitType !== OrgUnitType.WHOLESALE_PRICE_LIST &&
          orgUnitType !== OrgUnitType.RETAIL_BRAND,
        getColumn: () => salesPriceColumn,
      },
      {
        show: ({ orgUnitType }) => orgUnitType === OrgUnitType.WHOLESALE_PRICE_LIST,
        getColumn: () => MGLSalesPriceColumn,
      },
      {
        show: ({ orgUnitType }) => orgUnitType === OrgUnitType.WHOLESALE_PRICE_LIST,
        getColumn: () => MGLSalesCoverageColumn,
      },
      {
        show: ({ selectedView, orgUnitType }) =>
          orgUnitType !== OrgUnitType.WHOLESALE_PRICE_LIST &&
          (selectedView === 'ASSORTMENT_FULL' || selectedView === 'ASSORTMENT_SIMPLE'),
        getColumn: () => salesPriceCoverageColumn,
      },
      {
        show: ({ orgUnitType }) => orgUnitType === OrgUnitType.WHOLESALE_PRICE_LIST,
        getColumn: () => customerMarkupFactorColumn,
      },
      {
        show: ({ orgUnitType }) => orgUnitType === OrgUnitType.WHOLESALE_PRICE_LIST,
        getColumn: () => customerSalesPriceColumn,
      },
      {
        show: ({ orgUnitType }) => orgUnitType === OrgUnitType.WHOLESALE_PRICE_LIST,
        getColumn: () => customerSalesCoverageColumn,
      },
      {
        show: ({ selectedView, showBracketPriceColumns }) =>
          (selectedView === 'ASSORTMENT_FULL' || selectedView === 'ASSORTMENT_SIMPLE') &&
          showBracketPriceColumns,
        getColumn: () => ({
          ...baseColumnProps,
          width: 180,
          field: 'salesPriceDpak',
          headerName: t('myAssortment.columns.salesPriceDpak'),
          renderCell: ({ row }) => {
            const dPakSalesPrice = includeVat
              ? row.pricesIncVat.salesPriceSecondary
              : row.pricesExVat.salesPriceSecondary;
            return dPakSalesPrice ? (
              <StackedTextCell
                topLine={<PricePerUnit price={dPakSalesPrice} unit={row.primaryUnit} />}
                nullableBottomLine
                bottomLine={getLLDate(row.salesPriceDate)}
              />
            ) : (
              '-'
            );
          },
          valueGetter: (_, row) => row.pricesExVat.salesPriceSecondary,
        }),
      },
      {
        show: ({ selectedView, showBracketPriceColumns }) =>
          (selectedView === 'ASSORTMENT_FULL' || selectedView === 'ASSORTMENT_SIMPLE') &&
          showBracketPriceColumns,
        getColumn: () => ({
          ...baseColumnProps,
          cellClassName: () => dataGridBoldClass,
          width: 120,
          field: 'salesPriceDpakCoverage',
          headerName: t('myAssortment.columns.salesCoverageDpak'),
          renderCell: ({ row }) => {
            const salesPriceCoverage = row.coverageDegreeSecondary;

            return salesPriceCoverage
              ? `${formatPrice(Number(salesPriceCoverage.toFixed(2)), i18n.language)}%`
              : '-';
          },
          valueGetter: (_, row) => row.coverageDegreeSecondary,
        }),
      },
      {
        show: ({ selectedView, showBracketPriceColumns }) =>
          (selectedView === 'ASSORTMENT_FULL' || selectedView === 'ASSORTMENT_SIMPLE') &&
          showBracketPriceColumns,
        getColumn: () => ({
          ...baseColumnProps,
          width: 180,
          field: 'salesPriceTpak',
          headerName: t('myAssortment.columns.salesPriceTpak'),
          renderCell: ({ row }) => {
            const tPakSalesPrice = includeVat
              ? row.pricesIncVat.salesPriceTertiary
              : row.pricesExVat.salesPriceTertiary;
            return tPakSalesPrice ? (
              <StackedTextCell
                topLine={<PricePerUnit price={tPakSalesPrice} unit={row.primaryUnit} />}
                nullableBottomLine
                bottomLine={getLLDate(row.salesPriceDate)}
              />
            ) : (
              '-'
            );
          },
          valueGetter: (_, row) => row.pricesExVat.salesPriceTertiary,
        }),
      },
      {
        show: ({ selectedView, showBracketPriceColumns }) =>
          (selectedView === 'ASSORTMENT_FULL' || selectedView === 'ASSORTMENT_SIMPLE') &&
          showBracketPriceColumns,
        getColumn: () => ({
          ...baseColumnProps,
          cellClassName: () => dataGridBoldClass,
          width: 120,
          field: 'salesPriceTpakCoverage',
          headerName: t('myAssortment.columns.salesCoverageTpak'),
          renderCell: ({ row }) => {
            const salesPriceCoverage = row.coverageDegreeTertiary;

            return salesPriceCoverage
              ? `${formatPrice(Number(salesPriceCoverage.toFixed(2)), i18n.language)}%`
              : '-';
          },
          valueGetter: (_, row) => row.coverageDegreeTertiary,
        }),
      },
      {
        show: ({ selectedView, orgUnitType }) =>
          orgUnitType !== OrgUnitType.RETAIL_BRAND &&
          (selectedView === 'PRICE_CHANGES' || selectedView === 'ASSORTMENT_FULL'),
        getColumn: () => futureSalesPriceColumn,
      },
      {
        show: ({ selectedView, isPriceSuggestionEnabled }) =>
          (selectedView === 'ASSORTMENT_FULL' || selectedView === 'ASSORTMENT_SIMPLE') &&
          isPriceSuggestionEnabled,
        getColumn: () => priceSourceColumn,
      },
      {
        show: ({ selectedView }) => selectedView === 'PRICE_CHANGES',
        getColumn: () => ({
          ...baseColumnProps,
          field: 'changeInSalesPrice',
          headerName: t('myAssortment.columns.salesPriceChange'),
          headerAlign: 'center',
          renderCell: ({ row }) => {
            const salesPrice = row.pricesIncVat.salesPrice;
            const futurePrice = row.pricesIncVat.futureSalesPrice;

            if (!salesPrice || !futurePrice) return '-';
            else if (!!salesPrice && !!futurePrice) {
              const changeInSalesPrice = futurePrice - salesPrice;
              const percentChange =
                formatPrice(calculatePercentChange(salesPrice, futurePrice), i18n.language) + '%';
              return (
                <Stack flexDirection="row" gap={1} alignItems="center">
                  {changeInSalesPrice > 0 ? (
                    <IncreaseIcon color="warning" fontSize="small" />
                  ) : changeInSalesPrice < 0 ? (
                    <DecreaseIcon color="success" fontSize="small" />
                  ) : null}
                  <StackedTextCell
                    topLine={t('myAssortment.recommendedPrices.value', {
                      value: formatPrice(changeInSalesPrice, i18n.language),
                    })}
                    bottomLine={percentChange}
                  />
                </Stack>
              );
            } else {
              return '-';
            }
          },
          valueGetter: (_, row) => {
            const futurePrice = row.pricesIncVat.futureSalesPrice;
            const price = row.pricesIncVat.salesPrice;
            return futurePrice && price ? futurePrice - price : undefined;
          },
        }),
      },
      {
        show: ({ selectedView }) =>
          selectedView === 'ASSORTMENT_FULL' || selectedView === 'ASSORTMENT_SIMPLE',
        getColumn: () => ({
          ...baseColumnProps,
          width: 220,
          field: 'validPriority',
          headerName: t('myAssortment.columns.addedAndRemovedToAssortmentDate'),
          renderCell: ({ row }) => (
            <StackedTextCell
              topLine={getLLDate(row.priority.validFrom)}
              bottomLine={getLLDate(row.priority.validTo) ?? '-'}
            />
          ),
          valueGetter: (_, row) => row.priority.validFrom,
        }),
      },
      {
        show: ({ selectedView }) => selectedView === 'ASSORTMENT_FULL',
        getColumn: () => isStockedColumn,
      },
      {
        show: ({ selectedView }) => selectedView === 'ASSORTMENT_FULL',
        getColumn: () => ({
          ...baseColumnProps,
          width: 150,
          field: 'source',
          headerName: t('myAssortment.columns.source'),
        }),
      },

      {
        show: ({ selectedView }) => selectedView === 'ASSORTMENT_FULL',
        getColumn: () => ({
          ...baseColumnProps,
          width: 150,
          field: 'discontinuation',
          headerName: t('myAssortment.columns.discontinuation'),
          renderCell: ({ row }) => {
            return (
              <StackedTextCell
                nullableBottomLine
                topLine={
                  row.discontinuationStatus
                    ? getDiscontinuationStatus(row.discontinuationStatus)
                    : '-'
                }
                bottomLine={getLLDate(row.discontinuationDate)}
              />
            );
          },
          valueGetter: (_, row) => row.discontinuationDate,
        }),
      },
      {
        show: ({ selectedView }) => selectedView !== 'COMPETITOR_PRICES',
        getColumn: () => ({
          ...baseColumnProps,
          field: 'createdByName',
          headerName: t('myAssortment.columns.addedBy'),
          renderCell: ({ row }) => row.priority.addedBy,
          valueGetter: (_, row) => row.priority.addedBy,
        }),
      },
      {
        show: ({ selectedView }) =>
          selectedView === 'ASSORTMENT_FULL' || selectedView === 'ASSORTMENT_SIMPLE',
        getColumn: () => ({
          ...baseColumnProps,
          width: 160,
          field: 'assortmentTags',
          headerName: t('myAssortment.columns.subAssortments'),
          renderCell: ({ row }) => (
            <Stack flexDirection="row" flexWrap="wrap" gap={0.5}>
              {row.assortmentTags?.map((tag) => (
                <SubAssortmentChip key={tag.tagId} tagName={tag.tagName} />
              ))}
            </Stack>
          ),
          valueGetter: (_, row) => row.assortmentTags?.length ?? 0,
        }),
      },
      {
        show: ({ showMainSupplierName }) => showMainSupplierName,
        getColumn: () => ({
          ...baseColumnProps,
          width: 180,
          field: 'mainSupplierName',
          headerName: t('myAssortment.columns.mainSupplierName'),
          renderCell: ({ row }) => row.mainSupplierName ?? '-',
        }),
      },
      {
        show: () => true,
        getColumn: () => ({
          ...baseColumnProps,
          field: 'actions',
          width: 115,
          sortable: false,
          hideable: false,
          headerAlign: 'center',
          align: 'center',
          disableColumnMenu: true,
          renderCell: ({ row }) => (
            <AssortmentTableActions
              supplierIdForCalculus={isPriceCalculationEnabled ? row.supplier.id : undefined}
              editItem={editItem ? () => editItem(row) : undefined}
              editPrices={editPrices ? () => editPrices(row) : undefined}
              deleteItem={deleteItem ? () => deleteItem(row.mgSupplierItemId) : undefined}
              viewItemCalculusCard={
                rowIdForCalculusCard ? () => rowIdForCalculusCard(row.mgSupplierItemId) : undefined
              }
              viewItemProductCard={() => rowIdForProductCard(row.mgSupplierItemId)}
            />
          ),
        }),
      },
    ],
    [
      itemStatusColumn,
      itemIdsColumn,
      itemNameColumn,
      t,
      supplierColumn,
      productGroupColumn,
      discountGroupColumn,
      purchaseConditionColumn,
      purchasePriceColumn,
      futurePurchasePriceColumn,
      i18n.language,
      freightConditionColumn,
      costPriceInSalesUnitColumn,
      costPriceColumn,
      salesPriceColumn,
      MGLSalesPriceColumn,
      MGLSalesCoverageColumn,
      salesPriceCoverageColumn,
      customerMarkupFactorColumn,
      customerSalesPriceColumn,
      customerSalesCoverageColumn,
      includeVat,
      futureSalesPriceColumn,
      priceSourceColumn,
      isStockedColumn,
      isPriceCalculationEnabled,
      rowIdForCalculusCard,
      rowIdForProductCard,
      editItem,
      editPrices,
      deleteItem,
    ]
  );
};
